import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
    findLoginId,
    findLoginPw,
    getSmsAuthCommonRequest,
    getSmsAuthVerify,
    updatePasswordAfterFind
} from "api/api";
import { aesCBCEncrypt } from "utils/commonUtils";
import { FindPasswordChangeType, SingleResult } from "type/type";
import { InputWrapC, InputWrapR } from "components/common/CommonLayout";
import { OutlinePrimaryBtn, SolidPrimaryBtn, SolidRedBtn } from "components/button/Button";

const FindContainerMain = styled.main`
    width: 580px;
    padding: 3.75rem 4.375rem 2.5rem;
    background-color: ${({theme}) => theme.newTheme.text.btnText_w};
    .find-title {
        font-family: var(--font-nanumSquare-acB);
        color: ${({ theme }) => theme.newTheme.text.mainText};
        margin: 0;
        font-size: 1.375rem;
    }
    label {
        font-weight: 600;
    }
    .text-red {
        margin-top: 0;
    }
`;

const FindWrap = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    
    p {
        font-size: 1rem;
        padding: 1rem;
        text-align: center;
    }
`;

const passwordRegex = /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*?_]).{8,16}$/;

const UserChangePwViewer = ({ user }: any) => {

    const navigate = useNavigate();

    // 비밀번호 입력
    const [passwordObj, setPasswordObj] = useState<FindPasswordChangeType>({
        login_id: "",
        phone: "",
        new_password: "",
        new_password_confirm: ""
    });

    const [isPasswordOk, setIsPasswordOk] = useState(false);
    const [passwordErrorMsg, setPasswordErrorMsg] = useState("");

    // 비밀번호 입력값 보이기/가리기
    const [newEyeOnOff1, setNewEyeOnOff1] = useState(false);
    const [newEyeOnOff2, setNewEyeOnOff2] = useState(false);

    //useState에 적용되는 시간이 있어서 비밀번호/비밀번호 확인에 대한 handler 따로 적용
    function handleChangePw(e: React.ChangeEvent<HTMLInputElement>) {
        const { value } = e.target;
        
        const isOk = passwordRegex.test(value);

        if (isOk) {
            if (value === passwordObj.new_password_confirm) {
                setPasswordErrorMsg("");
                setIsPasswordOk(true);
            } else {
                setPasswordErrorMsg("비밀번호를 다시 확인해주세요.");
                setIsPasswordOk(false);
            }
        } else {
            setPasswordErrorMsg("비밀번호는 최소 8자에서 16자까지, 영문자/숫자/특수 문자를 포함해야 합니다.");
            setIsPasswordOk(false);
        }
          
        setPasswordObj((prev) => ({
            ...prev,
            new_password: value
        }));

    }

    function handleChangePw2(e: React.ChangeEvent<HTMLInputElement>) {
        const { value } = e.target;
        
        const isOk = passwordRegex.test(value);

        if (isOk) {
            if (passwordObj.new_password === value) {
                setPasswordErrorMsg("");
                setIsPasswordOk(true);
            } else {
                setPasswordErrorMsg("비밀번호를 다시 확인해주세요.");
                setIsPasswordOk(false);
            }
        } else {
            setPasswordErrorMsg("비밀번호는 최소 8자에서 16자까지, 영문자/숫자/특수 문자를 포함해야 합니다.");
            setIsPasswordOk(false);
        }
          
        setPasswordObj((prev) => ({
            ...prev,
            new_password_confirm: value
        }));

    }

    // 비밀번호 변경
    async function changeUserPw() {
        if (!isPasswordOk) return;

        try {
             //암호화
            const updateData = {
                login_id: user.login_id,
                phone: aesCBCEncrypt(user.phone),
                new_password: aesCBCEncrypt(passwordObj.new_password),
                new_password_confirm: aesCBCEncrypt(passwordObj.new_password_confirm),
            }
            const res = await updatePasswordAfterFind(updateData);

            if (res.code === 200) {
                alert("비밀번호가 변경되었습니다. 로그인페이지로 이동합니다.");
                navigate("/login");
            }
            else {
                alert(res.message);
                return;
            }
        } catch (error) {
            console.error(error);
        }
    }

    function handleActiveEnter(e: any) {
        if (e.key === 'Enter') changeUserPw();
    }

    return (
        <>
            <InputWrapC>
                <label htmlFor="new_password" style={{whiteSpace: "pre", width: '8.75rem'}}>신규 비밀번호</label>
                <input
                    type={!newEyeOnOff1 ? 'password' : 'text'}
                    name="new_password"
                    value={passwordObj.new_password}
                    onChange={handleChangePw}
                    onKeyDown={handleActiveEnter}
                    maxLength={16}
                    placeholder="신규 비밀번호 입력(8~16자리/대소문자/숫자/특수문자 포함)"
                />
                <div className='eye-on-off' onClick={() => setNewEyeOnOff1(prev => !prev)}>
                    {!newEyeOnOff1 ? <img src="/image/icon/eye_visible.svg" alt="eye_visible" /> : <img src="/image/icon/eye_invisible.svg" alt="eye_invisible" />}
                </div>
            </InputWrapC>
            <InputWrapC>
                <label htmlFor="new_password_confirm" style={{whiteSpace: "pre", width: '8.75rem'}}>신규 비밀번호 확인</label>
                <input
                    type={!newEyeOnOff2 ? 'password' : 'text'}
                    name="new_password_confirm"
                    value={passwordObj.new_password_confirm}
                    onChange={handleChangePw2}
                    onKeyDown={handleActiveEnter}
                    maxLength={16}
                    placeholder="신규 비밀번호 확인 입력(8~16자리/대소문자/숫자/특수문자 포함)"
                />
                <div className='eye-on-off' onClick={() => setNewEyeOnOff2(prev => !prev)}>
                    {!newEyeOnOff2 ? <img src="/image/icon/eye_visible.svg" alt="eye_visible" /> : <img src="/image/icon/eye_invisible.svg" alt="eye_invisible" />}
                </div>
                <p className={`text-red ${(passwordObj.new_password_confirm && !isPasswordOk) ? "show" : "none"}`} style={{fontSize: '0.75rem'}}>{passwordErrorMsg}</p>
            </InputWrapC>
            <SolidPrimaryBtn className="default-btn" onClick={changeUserPw} disabled={!isPasswordOk} >확인</SolidPrimaryBtn>
        </>
    )
}


export function FindLoginPw() {

    // 인증시간
    const MINUTES_IN_MS = 3 * 60 * 1000;
    const INTERVAL = 1000;
    const [leftTime, setLeftTime] = useState<number>(MINUTES_IN_MS);
    const [isFail, setIsFail] = useState(false);

    const mins = String(Math.floor((leftTime / (1000 * 60)) % 60)).padStart(2, "0");
    const sec = String(Math.floor((leftTime / 1000) % 60)).padStart(2, "0");

    // 인증 완료 후 비밀번호 변경 페이지로 이동용
    const [findUserPwEnd, setFindUserPwEnd] = useState(false);
    const [findUserPwObj, setFindUserPwObj] = useState({
        code: 0,
        data: ""
    });

    // 입력값
    const [inputData, setInputData] = useState<{name: string, login_id: string, phone: string}>({
        name: "", login_id: "", phone: "",
    });

    // 받은 인증번호
    const [authNumber, setAuthNumber] = useState("");
    // 입력값 입력 유효성 검사
    const [isEmptyData, setIsEmptyData] = useState(true);
    // 인증번호 요청 버튼 클릭
    const [clickSmsAuthBtn, setClickSmsAuthBtn] = useState(false);
    // 인증 버튼 클릭
    const [clickAuthBtn, setClickAuthBtn] = useState(false);
    // 입력한 인증번호
    const [authVerifyNumber, setAuthVerifyNumber] = useState("");
    
    // 인증번호 요청
    const smsRequest = async () => {
        setClickAuthBtn(true);
        setAuthVerifyNumber(""); // input 초기화
        setLeftTime(MINUTES_IN_MS); // 3분 초기화
        setIsFail(false);

        // 이름/핸드폰번호 인증번호 전송
        const result: SingleResult<string> = await getSmsAuthCommonRequest({
            name: inputData.name,
            login_id: inputData.login_id,
            phone: aesCBCEncrypt(inputData.phone),
            sms_type: "PASSWORD_FIND"
        });

        if (result.code === 200) setAuthNumber(result.data);
        else {
            alert(result.message);
            setAuthNumber("");
            setClickAuthBtn(false);
            setLeftTime(MINUTES_IN_MS); // 3분 초기화
            return;
        }
    };

    //이름/아이디 값 변경 handler
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setInputData(prev => ({...prev, [name]: value })); // 상태 업데이트
    };

    //핸드폰 번호 변경 handler
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 11) {
            setInputData(prev => ({...prev, phone: value})); // 상태 업데이트
        }
    };
    
    // 인증번호 입력
    function handleAuthChange(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 4) setAuthVerifyNumber(value);
    }

    // 인증번호 검증
    async function checkAuthNumber() {
        const result = await getSmsAuthVerify(aesCBCEncrypt(inputData.phone), authVerifyNumber);
        
        if (result.code === 200) {
            // 인증번호 검증 성공 시 아이디 확인
            const findPw = await findLoginPw({login_id: inputData.login_id, phone: aesCBCEncrypt(inputData.phone)});

            setFindUserPwObj({code: findPw.code, data: findPw.data ? findPw.data.login_id : ""});
            setFindUserPwEnd(true);

            setAuthNumber("");
            setClickAuthBtn(false);
            setLeftTime(MINUTES_IN_MS); // 3분 초기화
        }
        else if (result.code === 400) {
            alert("인증번호가 일치하지 않습니다.");
            return;
        }
        else {
            alert(result.message);
            return;
        }
    }

    // 인증 버튼을 누르면 INTERVAL 마다 실행
    useEffect(() => {
        if (!clickAuthBtn) return;

        const timer = setInterval(() => {
            setLeftTime((prev) => prev - INTERVAL);
        }, INTERVAL);
        if (leftTime <= 0) {
            clearInterval(timer);
            setIsFail(true);
            setClickAuthBtn(false);
        }
        return () => {
            clearInterval(timer);
        };
    }, [clickAuthBtn, leftTime]);

    useEffect(() => {
        if (inputData.name && inputData.login_id && inputData.phone && inputData.phone.length === 11) setIsEmptyData(false);
        else setIsEmptyData(true);
    }, [inputData]);

    useEffect(() => {
        if (!clickSmsAuthBtn) {
            setClickAuthBtn(false);
            setAuthVerifyNumber(""); // input 초기화
            setIsFail(false);
        }
    }, [clickSmsAuthBtn]);

    return (
        <FindContainerMain>
            <div className="find-title">
                <h3>비밀번호 찾기</h3>
            </div>
            {!findUserPwEnd ?
            <>
                <InputWrapC $gap="1.5rem">
                    <InputWrapC>
                        <label htmlFor="name">이름</label>
                        <input type="text" name="name" value={inputData.name} onChange={handleInputChange}
                            placeholder="이름 입력" />
                    </InputWrapC>
                    <InputWrapC>
                        <label htmlFor="login_id">아이디</label>
                        <input type="text" name="login_id" value={inputData.login_id} onChange={handleInputChange}
                            placeholder="아이디 입력" />
                    </InputWrapC>
                    <InputWrapR>
                        <InputWrapC>
                            <label>휴대폰</label>
                            <InputWrapC>
                                <InputWrapR>
                                    <input type="text" name="phone" value={inputData.phone} onChange={handlePhoneChange}
                                        placeholder="연락처 입력('-'없이 숫자만 입력)" disabled={clickSmsAuthBtn} style={{width: '100%'}}/>
                                    {!clickSmsAuthBtn ?
                                        <InputWrapR $justifyContent="flex-end" style={{width: 'auto', whiteSpace: 'nowrap'}}>
                                            <SolidPrimaryBtn onClick={() => { setClickSmsAuthBtn(true); smsRequest(); }} disabled={isEmptyData}>인증번호 요청</SolidPrimaryBtn>
                                        </InputWrapR>
                                        :
                                        <InputWrapR $justifyContent="flex-end" style={{width: 'auto', whiteSpace: 'nowrap'}}>
                                            <OutlinePrimaryBtn className="little-btn" onClick={() => setClickSmsAuthBtn(false)}>취소</OutlinePrimaryBtn>
                                        </InputWrapR>
                                    }
                                </InputWrapR>
                                {clickSmsAuthBtn &&
                                <>
                                    <InputWrapR>
                                        <input type="text" name="authNumber" value={authVerifyNumber} onChange={handleAuthChange}
                                            placeholder="인증번호 입력" style={{width: '100%'}} />
                                        <InputWrapR $justifyContent="flex-end" style={{width: 'auto', whiteSpace: 'nowrap'}}>
                                            <SolidRedBtn onClick={smsRequest}>인증번호 재요청</SolidRedBtn>
                                        </InputWrapR>
                                    </InputWrapR>
                                    <InputWrapR $gap="2rem" style={{paddingLeft: '1rem'}}>
                                        {clickAuthBtn && <p className="text-red">{mins} : {sec}</p>}
                                        {isFail && <p className="text-red">인증번호를 다시 받아주세요.</p>}
                                    </InputWrapR>
                                </>
                                }
                            </InputWrapC>
                        </InputWrapC>
                    </InputWrapR>
                </InputWrapC>
                <SolidPrimaryBtn type="button" className="default-btn" onClick={checkAuthNumber} disabled={!(authVerifyNumber.length === 4)} >다음</SolidPrimaryBtn>
            </>
            :
            <FindViewer type={'pw'} code={findUserPwObj.code} data={findUserPwObj.data} user={inputData} />
            
        }
        </FindContainerMain>
    );
}




const FindViewer = ({type, code, data, user}: any) => {
    const navigate = useNavigate();

    return (
        <>
            {type === 'id' ?
                code === 200 ?
                <>
                    <FindWrap>
                        <p>휴대폰 번호와 일치하는 아이디입니다.</p>
                        <p>아이디: <span style={{fontWeight: '600'}}>{data}</span></p>
                    </FindWrap>
                    <SolidPrimaryBtn type="button" className="default-btn" onClick={() => navigate("/login")}>로그인하기</SolidPrimaryBtn>
                </>
                :
                <>
                    <FindWrap>
                        <p>휴대폰 번호와 일치하는 아이디가 없습니다.</p>
                        <p>회원가입을 진행해주세요.</p>
                    </FindWrap>
                    <SolidPrimaryBtn type="button" className="default-btn" onClick={() => navigate("/login/sign")}>회원가입</SolidPrimaryBtn>
                </>
            :
                code !== 200 ?
                <>
                    <FindWrap>
                        <p><span>등록되지 않은 아이디 또는 </span><br /><span>휴대폰 번호와 일치하지 않는 아이디입니다.</span></p>
                        <p>아이디 찾기 또는 회원가입을 진행해주세요.</p>
                    </FindWrap>
                    <SolidPrimaryBtn type="button" className="default-btn" onClick={() => navigate("/login")}>로그인하기</SolidPrimaryBtn>
                </>
                :
                <UserChangePwViewer user={user} />
            }
        
        </>
    )
}

export function FindLoginId() {

    // 인증시간
    const MINUTES_IN_MS = 3 * 60 * 1000;
    const INTERVAL = 1000;
    const [leftTime, setLeftTime] = useState<number>(MINUTES_IN_MS);
    const [isFail, setIsFail] = useState(false);

    const mins = String(Math.floor((leftTime / (1000 * 60)) % 60)).padStart(2, "0");
    const sec = String(Math.floor((leftTime / 1000) % 60)).padStart(2, "0");

    // 아이디 찾기 페이지/찾은 아이디 보여주기
    const [findUserIdEnd, setFindUserIdEnd] = useState(false);
    const [findUserIdObj, setFindUserIdObj] = useState({
        code: 0,
        data: ""
    });

    // 입력값
    const [inputData, setInputData] = useState<{name: string, phone: string}>({
        name: "", phone: "",
    });

    // 받은 인증번호
    const [authNumber, setAuthNumber] = useState("");
    // 입력값 입력 유효성 검사
    const [isEmptyData, setIsEmptyData] = useState(true);
    // 인증번호 요청 버튼 클릭
    const [clickSmsAuthBtn, setClickSmsAuthBtn] = useState(false);
    // 인증 버튼 클릭
    const [clickAuthBtn, setClickAuthBtn] = useState(false);
    // 입력한 인증번호
    const [authVerifyNumber, setAuthVerifyNumber] = useState("");
    
    // 인증번호 요청
    const smsRequest = async () => {

        // 이름/핸드폰번호 인증번호 전송
        const result: SingleResult<string> = await getSmsAuthCommonRequest({
            name: inputData.name,
            phone: aesCBCEncrypt(inputData.phone),
            sms_type: "ID_FIND"
        });

        setClickAuthBtn(true);
        setAuthVerifyNumber(""); // input 초기화
        setLeftTime(MINUTES_IN_MS); // 3분 초기화
        setIsFail(false);


        if (result.code === 200) setAuthNumber(result.data);
        else {
            alert("해당 정보를 가진 사용자를 찾지 못했습니다. 올바른 정보로 다시 입력해주세요.");
            setAuthNumber("");
            setClickAuthBtn(false);
            setLeftTime(MINUTES_IN_MS); // 3분 초기화
            return;
        }
    };

    //이름 값 변경 handler
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setInputData(prev => ({...prev, name: value })); // 상태 업데이트
    };

    //핸드폰 번호 변경 handler
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 11) {
            setInputData(prev => ({...prev, phone: value})); // 상태 업데이트
        }
    };
    
    // 인증번호 입력
    function handleAuthChange(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 4) setAuthVerifyNumber(value);
    }

    function handleActiveEnter(e: any) {
        if (e.key === 'Enter') checkAuthNumber();
    }

    async function checkAuthNumber() {
        if (!authVerifyNumber) {
            alert("인증번호를 입력해주세요.");
            return;
        }
        const result = await getSmsAuthVerify(aesCBCEncrypt(inputData.phone), authVerifyNumber);
        
        if (result.code === 200) {
            const findId = await findLoginId({name: aesCBCEncrypt(inputData.name), phone: aesCBCEncrypt(inputData.phone)});

            setFindUserIdObj({code: findId.code, data: findId.data ? findId.data.login_id : ""});
            setFindUserIdEnd(true);

            setAuthNumber("");
            setClickAuthBtn(false);
            setLeftTime(MINUTES_IN_MS); // 3분 초기화
        }
        else if (result.code === 400) {
            alert("인증번호가 일치하지 않습니다.");
            return;
        }
        else {
            alert(result.message);
            return;
        }
    }

    // 인증 버튼을 누르면 INTERVAL 마다 실행
    useEffect(() => {
        if (!clickAuthBtn) return;

        const timer = setInterval(() => {
            setLeftTime((prev) => prev - INTERVAL);
        }, INTERVAL);
        if (leftTime <= 0) {
            clearInterval(timer);
            setIsFail(true);
            setClickAuthBtn(false);
        }
        return () => {
            clearInterval(timer);
        };
    }, [clickAuthBtn, leftTime]);

    useEffect(() => {
        if (inputData.name && inputData.phone && inputData.phone.length === 11) setIsEmptyData(false);
        else setIsEmptyData(true);
    }, [inputData]);

    useEffect(() => {
        if (!clickSmsAuthBtn) {
            setClickAuthBtn(false);
            setAuthVerifyNumber(""); // input 초기화
            setIsFail(false);
        }
    }, [clickSmsAuthBtn]);

    return (
        <FindContainerMain>
            <div className="find-title">
                <h3>아이디 찾기</h3>
            </div>
            {!findUserIdEnd ?
            <>
                <InputWrapC $gap="1.5rem">
                    <InputWrapC>
                        <label htmlFor="name">이름</label>
                        <input type="text" name="name" value={inputData.name} onChange={handleNameChange}
                            placeholder="이름 입력" />
                    </InputWrapC>
                    <InputWrapR>
                        <InputWrapC>
                            <label>휴대폰</label>
                            <InputWrapC>
                                <InputWrapR>
                                    <input type="text" name="phone" value={inputData.phone} onChange={handlePhoneChange}
                                        placeholder="연락처 입력('-'없이 숫자만 입력)" disabled={clickSmsAuthBtn} style={{width: '100%'}}/>
                                    {!clickSmsAuthBtn ?
                                        <InputWrapR $justifyContent="flex-end" style={{width: 'auto', whiteSpace: 'nowrap'}}>
                                            <SolidPrimaryBtn onClick={() => { setClickSmsAuthBtn(true); smsRequest(); }} disabled={isEmptyData}>인증번호 요청</SolidPrimaryBtn>
                                        </InputWrapR>
                                        :
                                        <InputWrapR $justifyContent="flex-end" style={{width: 'auto', whiteSpace: 'nowrap'}}>
                                            <OutlinePrimaryBtn className="little-btn" onClick={() => setClickSmsAuthBtn(false)}>취소</OutlinePrimaryBtn>
                                        </InputWrapR>
                                    }
                                </InputWrapR>
                                {clickSmsAuthBtn &&
                                <>
                                    <InputWrapR>
                                        <input type="text" name="authNumber" value={authVerifyNumber}
                                            onChange={handleAuthChange}
                                            onKeyDown={handleActiveEnter}
                                            placeholder="인증번호 입력" style={{width: '100%'}} />
                                        <InputWrapR $justifyContent="flex-end" style={{width: 'auto', whiteSpace: 'nowrap'}}>
                                            <SolidRedBtn onClick={smsRequest}>인증번호 재요청</SolidRedBtn>
                                        </InputWrapR>
                                    </InputWrapR>
                                    <InputWrapR $gap="2rem" style={{paddingLeft: '1rem'}}>
                                        {clickAuthBtn && <p className="text-red">{mins} : {sec}</p>}
                                        {isFail && <p className="text-red">인증번호를 다시 받아주세요.</p>}
                                    </InputWrapR>
                                </>
                                }
                            </InputWrapC>
                        </InputWrapC>
                    </InputWrapR>
                </InputWrapC>
                <SolidPrimaryBtn type="button" className="default-btn" onClick={checkAuthNumber} disabled={!(authVerifyNumber.length === 4)} >다음</SolidPrimaryBtn>
            </>
            :
            <FindViewer type={"id"} code={findUserIdObj.code} data={findUserIdObj.data} />
            
        }
        </FindContainerMain>
    );
}
